import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import classNames from "classnames";
import * as React from "react";
import { useRef, useState } from "react";

import {
  AddIcon,
  DeleteOutlineIcon,
  EditIcon,
  MoreVertIcon,
} from "@/shared/foundation/icons/mui";

import {
  CardConfig,
  CardKind,
  generateSectionId,
  Placement,
  SectionConfig,
} from "../DashboardHeader/DashboardParams";

import { AddCardPopover } from "./AddCardPopover";
import styles from "./DashboardSections.module.css";
import { useMarqueeSelect } from "./hooks/useMarqueeSelect";
import {
  CARD_DRAG_TYPE,
  DropCell,
  usePlacedReorder,
} from "./hooks/usePlacedReorder";
import { RESIZE_EDGES, usePlacedResize } from "./hooks/usePlacedResize";
import {
  reorderSections,
  SECTION_DRAG_TYPE,
  SectionAxis,
  useSectionReorder,
} from "./hooks/useSectionReorder";
import { SectionDeleteDialog } from "./SectionDeleteDialog";
import {
  Cell,
  colsFor,
  defaultAddCell,
  defaultSpanFor,
  EmptyCell,
  equalizeEmptyRows,
  fitsAt,
  fullHeightRows,
  GRID_GAP,
  GridLayout,
  MASTER_COLS,
  masterCellFor,
  packPlaced,
  PlacedCard,
  PlacementSave,
  preferredSize,
  sectionsPerRowFor,
  sectionWidthFor,
} from "./sectionLayout";

export interface LayoutChange {
  cards?: CardConfig[];
  sections?: SectionConfig[];
}

export interface DashboardSectionsProps {
  sections: SectionConfig[];
  cards: CardConfig[];
  /** Persist a layout edit (cards and/or sections) — one URL update. */
  onLayoutChange: (next: LayoutChange) => void;
  /** Render the body of one card (chart / trend tile content). */
  renderCardBody: (card: CardConfig) => React.ReactNode;
  /** Open the widget editor for an existing card. */
  onEditCard: (card: CardConfig) => void;
  /** Open the widget editor for a NEW card of `kind` in `section`, at the
   *  master-grid placement the clicked gap fitted it into. Resolved here
   *  rather than by the editor because only the rendered canvas knows which
   *  gaps are free. */
  onAddCard: (kind: CardKind, section: string, placement: Placement) => void;
}

/** Measured content width of an element, tracked through resizes. */
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const measure = () => setWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** Section title as an input styled like a heading, committed on blur/Enter so
 *  typing doesn't rewrite the URL on every keystroke. */
function SectionTitleInput({
  title,
  onCommit,
}: {
  title: string;
  onCommit: (next: string) => void;
}) {
  const [value, setValue] = useState(title);
  // Re-sync when a different saved title arrives (e.g. loading a dashboard).
  React.useEffect(() => setValue(title), [title]);
  return (
    <OutlinedInput
      className={styles.titleInput}
      value={value}
      inputProps={{ "aria-label": "Section title" }}
      size="small"
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value !== title) {
          onCommit(value);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

/** True when an event started on an interactive control inside a card — those
 *  own their pointer interactions, so drags/selection shouldn't hijack them. */
function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(
      "button, input, textarea, select, canvas, [role='combobox'], [role='listbox']",
    ) !== null
  );
}

/**
 * True when a `dragstart` began with the pointer over one of those controls.
 *
 * Asking `event.target` is not enough, and on a dragstart it is close to
 * useless: the HTML drag model fires `dragstart` at the drag's SOURCE NODE —
 * the nearest `draggable` ancestor, which is always this cell — rather than at
 * whatever the pointer was on. So `isInteractiveTarget(event.target)` sees the
 * cell, matches nothing, and the card starts dragging out from under a chart
 * the reader meant to pan. The event's pointer coordinates do still name the
 * real node, so ask the document what is there.
 *
 * Scoped to this cell with `contains`, which also makes the check harmless for
 * a synthesized drag carrying no coordinates: `elementFromPoint(0, 0)` lands
 * somewhere else on the page and is rejected rather than vetoing the drag.
 */
function dragStartedOnControl(event: React.DragEvent<HTMLElement>): boolean {
  const under = document.elementFromPoint(event.clientX, event.clientY);
  return (
    under !== null &&
    event.currentTarget.contains(under) &&
    isInteractiveTarget(under)
  );
}

const CARD_LABEL: Record<CardKind, string> = {
  trend: "trend card",
  chart: "chart card",
};

/**
 * The placed-layout substrate of the metrics dashboard.
 *
 * Renders titled SECTIONS, each an independently-packed 6-column master
 * canvas, laid out horizontally when the screen fits several side by side
 * (see `sectionLayout.ts` for the geometry). Cards carry explicit `{row, col,
 * w, h}` placements in master coordinates; interaction features layered on
 * top:
 *
 * - drag a card to any cell (of any section) — its footprint previews under
 *   the pointer, and dropping saves the equivalent master cell. Existing
 *   cards always keep their cells (least-recently-placed wins): a drop that
 *   would overlap one is prevented, and the preview turns grey;
 * - click a card to highlight it; dragging any highlighted card moves the
 *   whole highlighted group by one shared delta. The first click on anything
 *   that ISN'T a card — an empty cell, section chrome, or anywhere else on
 *   the page — clears the highlight;
 * - press on a section's empty space and DRAG to draw a marquee rectangle:
 *   the section's cards intersecting it are selected live (replacing the
 *   selection), and the rectangle's selection then drags as a group like a
 *   clicked one (see useMarqueeSelect);
 * - drag a card's edges/corners to resize it in whole cells — growth stops
 *   at the first neighboring card;
 * - hover an empty cell for a "+" that adds a new card right there at its
 *   default footprint (or the largest smaller one that fits); a kind whose
 *   minimum footprint doesn't fit at that cell (charts need 2×2) is grayed
 *   out;
 * - sections can be added (ghost slot at the end), renamed (editable title),
 *   and deleted — which also deletes their cards, so a non-empty section
 *   asks for confirmation first.
 */
export function DashboardSections({
  sections,
  cards,
  onLayoutChange,
  renderCardBody,
  onEditCard,
  onAddCard,
}: DashboardSectionsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);
  const masterCols = MASTER_COLS;

  // Width-derived split, capped at sections.length + 1 so a dashboard with
  // few sections never spreads into columns it can't fill (the +1 keeps the
  // Add-section ghost slot on the same row — it occupies a real grid track).
  const sectionsPerRow =
    width > 0 ? sectionsPerRowFor(width, sections.length) : 1;
  const sectionWidth = width > 0 ? sectionWidthFor(width, sectionsPerRow) : 0;
  // Infinity before first measure means "don't lay out yet"; cards render
  // unpositioned for one frame instead of jumping between tiers.
  const gridColumns = width > 0 ? colsFor(sectionWidth, masterCols) : Infinity;
  const measured = Number.isFinite(gridColumns);
  // One grid track in px — the same arithmetic the CSS --grid-track runs
  // against the section container. Provided to the cards as --track-size so
  // card TYPOGRAPHY can key off the grid density rather than the card's own
  // span: a multi-cell chart's title must match a 1×1 trend tile's. The cards
  // can't read --grid-track for this themselves — custom properties holding
  // container-relative units (100cqw) resolve at the POINT OF USE, so inside
  // a .cell (itself a size container) the same var would resolve against the
  // cell and grow with the card's span. A JS px value has no such trap.
  const trackSize = measured
    ? (sectionWidth - (gridColumns - 1) * GRID_GAP) / gridColumns
    : 0;

  const firstSectionId = sections[0]?.id ?? "";
  const knownSections = new Set(sections.map((s) => s.id));
  const sectionIdOf = (card: CardConfig): string =>
    card.section && knownSections.has(card.section)
      ? card.section
      : firstSectionId;
  const sectionCards = (sid: string) =>
    cards.filter((c) => sectionIdOf(c) === sid);
  const cardById = new Map(cards.map((c) => [c.id, c]));

  // Every section's layout, resolved fresh each render (placements live in the
  // URL-backed cards, so this is a pure projection).
  const sectionLayouts = new Map<string, GridLayout>();
  if (measured) {
    for (const s of sections) {
      sectionLayouts.set(
        s.id,
        packPlaced(sectionCards(s.id), gridColumns, masterCols),
      );
    }
    // When sections sit side by side, extend shorter sections' empty-cell
    // rows to match the tallest in each visual row — the CSS stretches the
    // section containers to equal height, and this fills the extra grid
    // height with add-card slots instead of blank space.
    equalizeEmptyRows(
      sectionLayouts,
      sections.map((s) => s.id),
      sectionsPerRow,
    );
  }
  const layoutFor = (sid: string): GridLayout | null =>
    sectionLayouts.get(sid) ?? null;

  // Highlighted (selected) cards. Clicking a card toggles it; dragging any
  // highlighted card moves the whole highlighted group together.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // The group that moves when `fromId` is dragged: the whole selection if it's
  // highlighted and there's more than one, else just that card.
  const dragGroup = (fromId: string) =>
    selected.has(fromId) && selected.size > 1 ? [...selected] : [fromId];

  // Marquee (rubber-band) selection: press on a section grid's empty space
  // and drag to draw a rectangle; cards intersecting it are selected live,
  // REPLACING the selection. Scoped to the section the gesture started in.
  // Never starts on a card — cards keep their click-to-toggle and HTML5
  // drag gestures.
  const marqueeSelect = useMarqueeSelect({
    shouldStartOn: (target) => target.closest(`.${styles.cell}`) === null,
    onMarqueeChange: setSelected,
  });
  const activeMarquee = marqueeSelect.marquee;
  const { consumeMarqueeEndClick } = marqueeSelect;

  /** Apply placement saves (and optional section reassignments / removals /
   *  additions) as one cards update. */
  const applyCardEdits = (edits: {
    saves?: PlacementSave[];
    reassign?: Map<string, string>;
    append?: CardConfig[];
  }) => {
    const byId = new Map((edits.saves ?? []).map((s) => [s.id, s.placement]));
    let next = cards.map((c) => {
      let out = c;
      const placement = byId.get(c.id);
      if (placement) {
        out = { ...out, placement };
      }
      const sid = edits.reassign?.get(c.id);
      if (sid) {
        out = { ...out, section: sid };
      }
      return out;
    });
    if (edits.append) {
      next = [...next, ...edits.append];
    }
    onLayoutChange({ cards: next });
  };

  /** The master layout map a card's placement edits are computed against. */
  const masterMapOf = (sid: string): ReadonlyMap<string, PlacedCard> =>
    layoutFor(sid)?.master ?? new Map();

  // Drop handling: dropping onto a cell saves the equivalent MASTER-canvas
  // cell (of the cell's section) as each moved card's placement. A single
  // card dropped into another section moves there; a highlighted GROUP
  // shifts by one shared delta within each member's own section, and only
  // commits when dropped in the dragged card's own section (matching the
  // footprint preview). The grab offset keeps the sub-cell the pointer
  // grabbed at dragstart under the pointer: the card's top-left lands that
  // many cells above/left of the hovered cell. Existing cards always keep
  // their cells (the least-recently-placed card wins): a drop whose footprint
  // would overlap any other card is prevented, not cascaded.
  const { draggingId, hoverCell, grabOffset, dragHandlers, dropHandlers } =
    usePlacedReorder((fromId, cell, grab) => {
      const toSid = cell.section;
      const toLayout = layoutFor(toSid);
      const fromCard = cardById.get(fromId);
      if (!toLayout || !fromCard) {
        return;
      }
      const fromSid = sectionIdOf(fromCard);
      const anchor = masterMapOf(fromSid).get(fromId);
      // Shift by the grab offset in RENDERED coordinates (clamped into the
      // grid) before translating to the master canvas, so the grabbed
      // sub-cell — not the top-left — lands on the hovered cell.
      const adjusted: DropCell = {
        row: Math.max(1, cell.row - grab.rows),
        col: Math.max(1, cell.col - grab.cols),
        section: cell.section,
      };
      const targetCell = masterCellFor(toLayout, adjusted, masterCols);
      const group = dragGroup(fromId);
      if (anchor && group.length > 1) {
        // A group never changes sections: the drop applies only when the
        // pointer is over the dragged card's OWN section, and hovering any
        // other section previews nothing (see renderFootprint) — preview
        // and drop must promise the same thing.
        if (toSid !== fromSid) {
          return;
        }
        // Shift every group member by the same delta WITHIN ITS OWN SECTION
        // (members selected from other sections shift within theirs — the
        // numeric delta is meaningful because every section shares the same
        // master-canvas geometry), clamped so all stay in bounds.
        let dRow = targetCell.row - anchor.rowStart;
        let dCol = targetCell.col - anchor.colStart;
        let minDCol = -Infinity;
        let maxDCol = Infinity;
        let minDRow = -Infinity;
        const memberMaster = (id: string) => {
          const card = cardById.get(id);
          return card ? masterMapOf(sectionIdOf(card)).get(id) : undefined;
        };
        for (const id of group) {
          const p = memberMaster(id);
          if (!p) {
            continue;
          }
          minDCol = Math.max(minDCol, 1 - p.colStart);
          maxDCol = Math.min(maxDCol, masterCols - p.cols - p.colStart + 1);
          minDRow = Math.max(minDRow, 1 - p.rowStart);
        }
        dCol = Math.min(Math.max(dCol, minDCol), maxDCol);
        dRow = Math.max(dRow, minDRow);
        // Every member must land on free cells (members ignore each other —
        // they move as one), else the whole group drop is prevented.
        const groupIds = new Set(group);
        const saves: PlacementSave[] = [];
        for (const id of group) {
          const p = memberMaster(id);
          const card = cardById.get(id);
          if (!p || !card) {
            continue;
          }
          const placement: Placement = {
            row: p.rowStart + dRow,
            col: p.colStart + dCol,
            w: p.cols,
            h: p.rows,
          };
          if (
            !fitsAt(
              masterMapOf(sectionIdOf(card)),
              placement,
              placement.w,
              placement.h,
              masterCols,
              groupIds,
            )
          ) {
            return;
          }
          saves.push({ id, placement });
        }
        applyCardEdits({ saves });
      } else {
        const placement: Placement = {
          row: targetCell.row,
          col: targetCell.col,
          w: anchor?.cols ?? defaultSpanFor(fromCard.kind, masterCols),
          h: anchor?.rows ?? preferredSize(fromCard.kind).defaultRows,
        };
        if (
          !fitsAt(
            masterMapOf(toSid),
            placement,
            placement.w,
            placement.h,
            masterCols,
            new Set([fromId]),
          )
        ) {
          return;
        }
        const reassign =
          toSid !== fromSid ? new Map([[fromId, toSid]]) : undefined;
        applyCardEdits({ saves: [{ id: fromId, placement }], reassign });
      }
    });

  const dragging = draggingId !== null;

  // The FIRST click on anything that isn't a card clears the highlight —
  // empty cells (their "Add card here" flow still opens), section chrome,
  // or anywhere else on the page. Listens on the document so clicks outside
  // this component clear too, in the capture phase so a handler that stops
  // propagation can't keep the selection alive. Clicks that land inside a
  // card cell are excluded — toggle/extend semantics stay with the card's
  // own onClick — and clicks during a drag are ignored (a real drag
  // suppresses the click, but a drop cancelled mid-gesture may not).
  const hasSelection = selected.size > 0;
  React.useEffect(() => {
    if (!hasSelection) {
      return;
    }
    const clearUnlessCard = (e: MouseEvent) => {
      if (dragging) {
        return;
      }
      // The click the browser fires as a completed marquee gesture ends must
      // not immediately clear the selection the marquee just made.
      if (consumeMarqueeEndClick()) {
        return;
      }
      if (e.target instanceof Element && e.target.closest(`.${styles.cell}`)) {
        return;
      }
      setSelected(new Set());
    };
    document.addEventListener("click", clearUnlessCard, true);
    return () => document.removeEventListener("click", clearUnlessCard, true);
  }, [hasSelection, dragging, consumeMarqueeEndClick]);

  // Whole-SECTION reordering: a drag from a section's own chrome (its padding
  // ring, header background, or the ⋮⋮ grip) picks the section up, and a blue
  // divider in the sections-grid gap previews the GAP it would land in. The
  // drop reorders the URL-backed sections array — cards travel with their
  // section (they reference it by id), so no placement changes. An in-place
  // landing returns null from reorderSections and writes nothing.
  const sectionReorder = useSectionReorder((fromId, dropIndex) => {
    const next = reorderSections(sections, fromId, dropIndex);
    if (next) {
      // Persist the cards too: their section assignments were made explicit
      // at load time (see assignSections in DashboardParams), and writing
      // them alongside the new order pins every card to its section — a
      // reorder must never re-home cards via the first-section fallback.
      onLayoutChange({ sections: next, cards });
    }
  });
  const sectionAxis: SectionAxis = sectionsPerRow > 1 ? "x" : "y";
  const sectionDragging = sectionReorder.draggingSectionId !== null;

  /** The blue insertion divider previewing gap `dropIndex`, rendered inside
   *  the section that anchors it: BEFORE section `dropIndex`, or AFTER the
   *  last section for the end gap. It lives in the 20px sections-grid gap —
   *  horizontal when sections stack, vertical when they sit side by side. */
  const renderSectionDivider = (index: number) => {
    const gap = sectionReorder.dropIndex;
    if (!sectionDragging || gap === null) {
      return null;
    }
    const before = gap === index;
    const after = gap === sections.length && index === sections.length - 1;
    if (!before && !after) {
      return null;
    }
    const classes = [
      styles.dropDivider,
      sectionAxis === "y" ? styles.dividerH : styles.dividerV,
      after ? styles.dividerAfter : styles.dividerBefore,
    ];
    return (
      <div data-testid="section-drop-divider" className={classes.join(" ")} />
    );
  };

  // Edge resizing. `isResizing()` lets us cancel the card drag that a handle
  // press would otherwise start (the card is draggable for reorder). A resize
  // step that would overlap another card is skipped — the card grows live
  // until it meets a neighbor, and shrinking back re-enables growth.
  const { isResizing, startResize } = usePlacedResize((id, placement) => {
    const card = cardById.get(id);
    if (!card) {
      return;
    }
    if (
      !fitsAt(
        masterMapOf(sectionIdOf(card)),
        placement,
        placement.w,
        placement.h,
        masterCols,
        new Set([id]),
      )
    ) {
      return;
    }
    applyCardEdits({ saves: [{ id, placement }] });
  });

  // The "+" over every empty cell adds a card placed at that cell. Which card
  // kinds fit there (a chart needs at least a free 2×2 block — its minimum
  // footprint) is computed when the flow opens; a kind whose minimum doesn't
  // fit renders grayed out. The snapshot stays valid for the flow's lifetime:
  // the popover holds the pointer, so nothing can re-pack the grid under it.
  const [addMenu, setAddMenu] = useState<{
    position: { left: number; top: number };
    cell: DropCell;
    fits: Record<CardKind, boolean>;
  } | null>(null);

  /** The master footprint a fresh card of `kind` would take at `cell`: the
   *  kind's default footprint when it fits there, else the largest smaller
   *  footprint that does. The shrink preserves the default's aspect: BOTH
   *  axes step down together (each clamped at its minimum), so a chart tries
   *  3×3, then 2×2 — a 2-wide gap gets a 2×2 chart, not a 2×3 sliver. Null
   *  when even the minimum footprint doesn't fit.
   *
   *  In shrunk (narrow) mode, the clicked cell's position is irrelevant —
   *  the 2-column rendering has no spatial meaning in the 6-column master.
   *  Instead, scan the master grid for the first open position that fits
   *  (row-by-row, column-by-column), like `packCells` does. */
  const insertionAt = (kind: CardKind, cell: DropCell): Placement | null => {
    const layout = layoutFor(cell.section);
    if (!layout) {
      return null;
    }
    const size = preferredSize(kind);
    const masterMap = masterMapOf(cell.section);
    const defaultW = Math.max(defaultSpanFor(kind, masterCols), size.minCols);

    if (layout.shrunk) {
      // Compute the master grid's highest occupied row.
      let masterMaxRow = 0;
      for (const p of masterMap.values()) {
        masterMaxRow = Math.max(masterMaxRow, p.rowStart + p.rows - 1);
      }
      for (let step = 0; ; step++) {
        const w = Math.max(defaultW - step, size.minCols);
        const h = Math.max(size.defaultRows - step, size.minRows);
        for (let r = 1; r <= masterMaxRow + 1; r++) {
          for (let c = 1; c + w - 1 <= masterCols; c++) {
            if (fitsAt(masterMap, { row: r, col: c }, w, h, masterCols)) {
              return { row: r, col: c, w, h };
            }
          }
        }
        if (w === size.minCols && h === size.minRows) {
          return null;
        }
      }
    }

    // Full-width mode: place at the exact clicked position.
    const at = masterCellFor(layout, cell, masterCols);
    for (let step = 0; ; step++) {
      const w = Math.max(defaultW - step, size.minCols);
      const h = Math.max(size.defaultRows - step, size.minRows);
      if (fitsAt(masterMap, at, w, h, masterCols)) {
        return { row: at.row, col: at.col, w, h };
      }
      if (w === size.minCols && h === size.minRows) {
        return null;
      }
    }
  };

  /** True when `kind`'s MINIMUM footprint fits at `cell` — i.e. when
   *  {@link insertionAt} has some footprint to place (any fitting footprint
   *  contains the minimum one). */
  const insertionFits = (kind: CardKind, cell: DropCell): boolean =>
    insertionAt(kind, cell) !== null;

  const removeCard = (id: string) => {
    setSelected((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    onLayoutChange({ cards: cards.filter((c) => c.id !== id) });
  };

  const addSection = () => {
    onLayoutChange({
      sections: [
        ...sections,
        { id: generateSectionId(), title: `Section ${sections.length + 1}` },
      ],
    });
  };

  const renameSection = (id: string, title: string) => {
    onLayoutChange({
      sections: sections.map((s) => (s.id === id ? { ...s, title } : s)),
    });
  };

  /** Delete a section: removes it AND its cards (same finality as card
   *  removal). The last section can't be deleted — the dashboard always has
   *  one. */
  const deleteSection = (id: string) => {
    if (sections.length <= 1) {
      return;
    }
    const doomed = new Set(sectionCards(id).map((c) => c.id));
    onLayoutChange({
      sections: sections.filter((s) => s.id !== id),
      cards: cards.filter((c) => !doomed.has(c.id)),
    });
  };

  // The section pending delete-confirmation (null when the dialog is closed).
  const [confirmDeleteSectionId, setConfirmDeleteSectionId] = useState<
    string | null
  >(null);
  const confirmDeleteSection =
    sections.find((s) => s.id === confirmDeleteSectionId) ?? null;

  // TODO(ENG-2765): section deletion is confirmed via dialog because it's
  // irreversible today. Once the dashboard editor has an undo/redo stack,
  // deleting a section will be undoable and this confirmation can go away.
  const requestDeleteSection = (id: string) => {
    if (sectionCards(id).length === 0) {
      // Only the empty container is destroyed — no more than an unconfirmed
      // single-card delete — so skip the dialog.
      deleteSection(id);
      return;
    }
    setConfirmDeleteSectionId(id);
  };

  /** After a confirmed deletion the menu button that anchored the dialog is
   *  gone, so MUI's focus restore would strand focus on `document.body`.
   *  Move it to the adjacent surviving section's header (its ⋮ button), or
   *  the always-present "Add section" button when none remains. */
  const focusAfterSectionDelete = (deletedIndex: number) => {
    requestAnimationFrame(() => {
      const root = containerRef.current;
      if (!root) {
        return;
      }
      const headers = root.querySelectorAll<HTMLElement>(
        `.${styles.sectionHeader}`,
      );
      const header = headers[Math.min(deletedIndex, headers.length - 1)];
      const target =
        header?.querySelector<HTMLElement>("button") ??
        root.querySelector<HTMLElement>(`.${styles.addSection}`);
      target?.focus();
    });
  };

  const renderCard = (card: CardConfig, sid: string, layout: GridLayout) => {
    const placed = layout.placements.get(card.id);
    const masterPlaced = layout.master.get(card.id) ?? placed;
    const inDragGroup =
      dragging &&
      draggingId !== null &&
      dragGroup(draggingId).includes(card.id);
    const classes = [styles.cell];
    if (selected.has(card.id)) {
      classes.push(styles.cellSelected);
    }
    if (inDragGroup) {
      classes.push(styles.cellDragging);
    }
    const style: React.CSSProperties | undefined = placed
      ? {
          gridColumn: `${placed.colStart} / span ${placed.cols}`,
          gridRow: `${placed.rowStart} / span ${placed.rows}`,
        }
      : undefined;
    const cell: DropCell | undefined = placed
      ? { row: placed.rowStart, col: placed.colStart, section: sid }
      : undefined;
    const drag = dragHandlers(
      card.id,
      placed ? { rows: placed.rows, cols: placed.cols } : undefined,
    );
    return (
      <div
        key={card.id}
        data-card-id={card.id}
        className={classes.join(" ")}
        style={style}
        {...drag}
        {...(cell ? dropHandlers(cell) : {})}
        onDragStart={(e) => {
          // A handle press starts a resize via pointer events, and interactive
          // controls own their pointer gestures; cancel the HTML5 drag the
          // (draggable) card would otherwise begin. A chart is one of those
          // controls — it pans and zooms under the same left-drag — and only
          // `dragStartedOnControl` can see that, for the reason given there.
          if (
            isResizing() ||
            isInteractiveTarget(e.target) ||
            dragStartedOnControl(e)
          ) {
            e.preventDefault();
            return;
          }
          drag.onDragStart(e);
        }}
        onClick={(e) => {
          // Click a card body to toggle its highlight. Ignore clicks on
          // controls (pickers, buttons, chart) and resize handles — and
          // clicks that only bubbled here through a React PORTAL (menus,
          // popovers, dialogs opened from inside the card): those targets
          // aren't inside the cell's DOM.
          if (
            !(e.target instanceof Element) ||
            !e.currentTarget.contains(e.target) ||
            isInteractiveTarget(e.target) ||
            e.target.closest(`.${styles.resizeHandle}`)
          ) {
            return;
          }
          toggleSelect(card.id);
        }}
      >
        <div className={styles.cellBody}>{renderCardBody(card)}</div>
        <CardMenu
          kind={card.kind}
          onEdit={() => onEditCard(card)}
          onDelete={() => removeCard(card.id)}
        />
        {/* Edge + corner resize handles (charts only — trend KPI tiles are
            locked to 1×1, see packPlaced). Kept mounted during a drag (hidden
            via CSS) so the dragged card's subtree doesn't change mid-gesture,
            which would cancel the native drag. The gesture edits the MASTER
            placement: on a compressed canvas the pointer is measured in
            rendered tracks and each track crossed counts as one master cell. */}
        {card.kind !== "trend" && placed && masterPlaced
          ? RESIZE_EDGES.map((edge) => (
              <div
                key={`rz-${edge}`}
                className={`${styles.resizeHandle} ${styles[`resize-${edge}`]}`}
                draggable={false}
                onPointerDown={(e) =>
                  startResize(
                    e,
                    card.id,
                    edge,
                    {
                      row: masterPlaced.rowStart,
                      col: masterPlaced.colStart,
                      w: masterPlaced.cols,
                      h: masterPlaced.rows,
                    },
                    masterCols,
                    { w: placed.cols, h: placed.rows },
                  )
                }
              />
            ))
          : null}
      </div>
    );
  };

  const renderEmpties = (sid: string, layout: GridLayout) => {
    // The cells the moving cards currently occupy are also drop targets, so a
    // large card can be nudged one cell (e.g. right or down) by dropping onto
    // its own footprint — otherwise those cells count as "occupied" and offer
    // nowhere to land nearby. One per-cell target per footprint cell of every
    // drag-group member, each in the member's own section; the moving cards'
    // cells ignore the pointer meanwhile (.gridDragging .cellDragging), so
    // hovering anywhere over a moving card resolves to the ACTUAL sub-cell
    // underneath, not the card's pinned top-left target.
    const selfEmpties: EmptyCell[] = [];
    if (draggingId !== null) {
      for (const id of dragGroup(draggingId)) {
        const member = cardById.get(id);
        if (!member || sectionIdOf(member) !== sid) {
          continue;
        }
        const p = layout.placements.get(id);
        if (!p) {
          continue;
        }
        for (let r = p.rowStart; r < p.rowStart + p.rows; r++) {
          for (let c = p.colStart; c < p.colStart + p.cols; c++) {
            selfEmpties.push({
              key: `se-${id}-${r}-${c}`,
              colStart: c,
              rowStart: r,
            });
          }
        }
      }
    }
    // One slot per section carries the resting "+ Add card" label — the one
    // just past the section's content. In a section with no cards that's the
    // top-left slot, and it gets accented too, so an otherwise blank canvas
    // offers an obvious place to start.
    const noCards = layout.placements.size === 0;
    const defaultAdd = defaultAddCell(layout);
    return [...layout.empties, ...selfEmpties].map((e) => (
      <div
        key={e.key}
        className={classNames(styles.cellEmpty, {
          [styles.cellEmptyDefault]: e === defaultAdd,
          [styles.cellEmptyPrimary]: e === defaultAdd && noCards,
        })}
        style={{
          gridColumn: `${e.colStart} / span 1`,
          gridRow: `${e.rowStart} / span 1`,
        }}
        {...dropHandlers({ row: e.rowStart, col: e.colStart, section: sid })}
      >
        {!dragging && (
          <button
            type="button"
            className={styles.emptyAdd}
            aria-label="Add card here"
            onClick={(ev) => {
              const cell = { row: e.rowStart, col: e.colStart, section: sid };
              setAddMenu({
                position: { left: ev.clientX, top: ev.clientY },
                cell,
                fits: {
                  trend: insertionFits("trend", cell),
                  chart: insertionFits("chart", cell),
                },
              });
            }}
          >
            <AddIcon fontSize="small" />
            <span>Add card</span>
          </button>
        )}
      </div>
    ));
  };

  // Footprint preview: while dragging, highlight every cell the dragged card
  // — and the rest of the highlighted group — would occupy if dropped now.
  // Accent when everything fits, grey when any part overflows OR would
  // overlap a card that isn't moving (the drop would be prevented). The
  // preview promises exactly what the drop commits:
  // - a SINGLE card previews in the hovered section (it moves there);
  // - a GROUP only moves while the pointer is over the dragged card's own
  //   section — each member then previews in ITS own section at the shared
  //   drop delta — and previews nothing anywhere when the pointer hovers a
  //   different section (that drop is prevented; groups never change
  //   sections).
  const renderFootprint = (sid: string, layout: GridLayout) => {
    if (!dragging || !hoverCell || draggingId === null) {
      return null;
    }
    const draggedCard = cardById.get(draggingId);
    if (!draggedCard) {
      return null;
    }
    const fromSid = sectionIdOf(draggedCard);
    const srcLayout = layoutFor(fromSid);
    const anchor = srcLayout?.placements.get(draggingId);
    if (!anchor) {
      return null;
    }
    const group = new Set(dragGroup(draggingId));
    const isGroup = group.size > 1;
    // Where would a drop land right now? A group's drop only applies with
    // the pointer over the SOURCE section (and then previews in every
    // member's section); a single card's drop applies to the hovered
    // section, so only that section previews.
    if (isGroup ? hoverCell.section !== fromSid : hoverCell.section !== sid) {
      return null;
    }
    // Rendered cells occupied by cards that are NOT part of the drag — the
    // cells the footprint may not land on.
    const occupied = new Set<string>();
    for (const [id, p] of layout.placements) {
      if (group.has(id)) {
        continue;
      }
      for (let r = p.rowStart; r < p.rowStart + p.rows; r++) {
        for (let c = p.colStart; c < p.colStart + p.cols; c++) {
          occupied.add(`${r}-${c}`);
        }
      }
    }
    // The dragged card's top-left previews `grabOffset` cells above/left of
    // the hovered cell (clamped into the grid) — the same adjustment the drop
    // applies — so the footprint sits around the pointer where it will land.
    const targetRow = Math.max(1, hoverCell.row - grabOffset.rows);
    const targetCol = Math.max(1, hoverCell.col - grabOffset.cols);
    const cells: Cell[] = [];
    let fits = true;
    if (!isGroup && fromSid !== sid) {
      // Cross-section single card: it lands at the (grab-adjusted) hovered
      // cell of this (the hovered) section.
      if (targetCol + anchor.cols - 1 > layout.columns) {
        fits = false;
      }
      for (let dr = 0; dr < anchor.rows; dr++) {
        for (let dc = 0; dc < anchor.cols; dc++) {
          const c = targetCol + dc;
          const r = targetRow + dr;
          if (c >= 1 && c <= layout.columns && r >= 1) {
            cells.push({ row: r, col: c });
          }
        }
      }
    } else {
      const dRow = targetRow - anchor.rowStart;
      const dCol = targetCol - anchor.colStart;
      for (const id of dragGroup(draggingId)) {
        const member = cardById.get(id);
        if (!member || sectionIdOf(member) !== sid) {
          continue; // members preview in their own section
        }
        const p = layout.placements.get(id);
        if (!p) {
          continue;
        }
        const nr = p.rowStart + dRow;
        const nc = p.colStart + dCol;
        if (nr < 1 || nc < 1 || nc + p.cols - 1 > layout.columns) {
          fits = false;
        }
        for (let dr = 0; dr < p.rows; dr++) {
          for (let dc = 0; dc < p.cols; dc++) {
            const c = nc + dc;
            const r = nr + dr;
            if (c >= 1 && c <= layout.columns && r >= 1) {
              cells.push({ row: r, col: c });
            }
          }
        }
      }
    }
    if (cells.some(({ row, col }) => occupied.has(`${row}-${col}`))) {
      fits = false;
    }
    return cells.map(({ row, col }) => (
      <div
        key={`fp-${row}-${col}`}
        className={`${styles.footprint} ${fits ? "" : styles.footprintInvalid}`}
        style={{ gridColumn: `${col} / span 1`, gridRow: `${row} / span 1` }}
      />
    ));
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.sections} ${sectionDragging ? styles.sectionsReordering : ""} ${activeMarquee !== null ? styles.sectionsMarqueeing : ""}`}
      style={{
        gridTemplateColumns: `repeat(${sectionsPerRow}, minmax(0, 1fr))`,
        ...(measured
          ? {
              "--grid-cols": String(gridColumns),
              "--track-size": `${trackSize}px`,
            }
          : {}),
      }}
      onDragOver={(e) => {
        // Accept section drags over the container's own surface (the 20px
        // gaps between sections) so the cursor doesn't flash no-drop there;
        // releasing in a gap commits via the dragend fallback at the last
        // divider position.
        if (Array.from(e.dataTransfer.types).includes(SECTION_DRAG_TYPE)) {
          e.preventDefault();
        }
      }}
    >
      {sections.map((s, index) => {
        const layout = layoutFor(s.id);
        const sectionDrag = sectionReorder.dragHandlers(s.id);
        const sectionClasses = [styles.section];
        if (sectionReorder.draggingSectionId === s.id) {
          sectionClasses.push(styles.sectionDragging);
        }
        return (
          <section
            className={sectionClasses.join(" ")}
            key={s.id}
            {...sectionDrag}
            {...sectionReorder.dropHandlers(index, sectionAxis)}
            onDragStart={(e) => {
              // The section drags from its own CHROME only — the padding
              // ring and the header background.
              if (e.target instanceof Element && e.target !== e.currentTarget) {
                // A nested drag source (a card cell) is the real source of
                // this bubbled dragstart — leave the card's gesture alone:
                // no state, no dataTransfer, and no preventDefault (that
                // would cancel the card's native drag).
                const inner = e.target.closest("[draggable='true']");
                if (inner !== null && inner !== e.currentTarget) {
                  return;
                }
                // Content that owns its pointer interactions — anything in
                // the card grid, the title input, a button/menu — must not
                // be hijacked: cancel the drag the (draggable) section would
                // otherwise begin, so text selection / clicks proceed.
                if (
                  e.target.closest(
                    `.${styles.grid}, input, textarea, button`,
                  ) !== null
                ) {
                  e.preventDefault();
                  return;
                }
              }
              sectionDrag.onDragStart(e);
            }}
          >
            {renderSectionDivider(index)}
            <header className={styles.sectionHeader}>
              <SectionTitleInput
                title={s.title}
                onCommit={(title) => renameSection(s.id, title)}
              />
              {sections.length > 1 && (
                <SectionMenu onDelete={() => requestDeleteSection(s.id)} />
              )}
            </header>
            <div
              className={classNames(styles.grid, {
                [styles.gridDragging]: dragging,
              })}
              style={
                layout
                  ? {
                      gridTemplateRows: `repeat(${fullHeightRows(layout)}, var(--grid-row-h))`,
                    }
                  : undefined
              }
              {...marqueeSelect.gridHandlers(s.id)}
              onDragOver={(e) => {
                // Accept card drags over the grid's own surface (the small
                // gaps between cells) so releasing there commits via the
                // dragend fallback at the last hovered cell, instead of
                // reading as a cancelled drag (dropEffect "none").
                if (Array.from(e.dataTransfer.types).includes(CARD_DRAG_TYPE)) {
                  e.preventDefault();
                }
              }}
            >
              {layout ? (
                <>
                  {sectionCards(s.id).map((card) =>
                    renderCard(card, s.id, layout),
                  )}
                  {renderEmpties(s.id, layout)}
                  {renderFootprint(s.id, layout)}
                </>
              ) : null}
              {activeMarquee !== null && activeMarquee.sectionId === s.id && (
                <div
                  data-testid="marquee-rect"
                  className={styles.marquee}
                  style={{
                    left: activeMarquee.rect.left,
                    top: activeMarquee.rect.top,
                    width: activeMarquee.rect.width,
                    height: activeMarquee.rect.height,
                  }}
                />
              )}
            </div>
          </section>
        );
      })}
      <button
        type="button"
        className={styles.addSection}
        onClick={addSection}
        // Dropping a section on the ghost slot means "move it to the end".
        {...sectionReorder.dropHandlers(sections.length, sectionAxis, {
          fixedGap: sections.length,
        })}
      >
        <AddIcon fontSize="small" />
        <span>Add section</span>
      </button>

      {addMenu !== null && (
        <AddCardPopover
          position={addMenu.position}
          fits={addMenu.fits}
          onPickKind={(kind) => {
            const placement = insertionAt(kind, addMenu.cell);
            // The menu only enables kinds that fit; this guards a click racing
            // a layout change underneath.
            if (placement !== null) {
              onAddCard(kind, addMenu.cell.section, placement);
            }
            setAddMenu(null);
          }}
          onClose={() => setAddMenu(null)}
        />
      )}

      {confirmDeleteSection !== null && (
        <SectionDeleteDialog
          title={confirmDeleteSection.title}
          cardCount={sectionCards(confirmDeleteSection.id).length}
          onClose={() => setConfirmDeleteSectionId(null)}
          onConfirm={() => {
            const index = sections.findIndex(
              (s) => s.id === confirmDeleteSection.id,
            );
            deleteSection(confirmDeleteSection.id);
            setConfirmDeleteSectionId(null);
            focusAfterSectionDelete(index);
          }}
        />
      )}
    </div>
  );
}

/** Every card's ⋮ menu (revealed on cell hover): Edit Data / Delete Card. */
function CardMenu({
  kind,
  onEdit,
  onDelete,
}: {
  kind: CardKind;
  onEdit: (anchorEl: HTMLElement) => void;
  onDelete: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        className={styles.cardMenu}
        aria-label={`${CARD_LABEL[kind]} menu`}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            const anchorEl = anchor;
            setAnchor(null);
            if (anchorEl) {
              onEdit(anchorEl);
            }
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Data" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onDelete();
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete Card" />
        </MenuItem>
      </Menu>
    </>
  );
}

/** The section header's ⋮ menu: just "Delete section" for now. */
function SectionMenu({ onDelete }: { onDelete: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        className={styles.sectionMenuButton}
        aria-label="Section options"
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onDelete();
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete section" />
        </MenuItem>
      </Menu>
    </>
  );
}
